import type { GridCell, GridCursor } from './model'
import type { GridFontVariant, GridRendererConfig, GridRgb } from './config'
import { boxSpec, visitBoxRects, type BoxRectSource, type BoxSpec } from './boxglyph'
import { colorsEqual, resolveCellColors } from './theme'

export enum GridStyle {
  Bold = 1 << 0,
  Dim = 1 << 1,
  Italic = 1 << 2,
  Underline = 1 << 3,
  Blink = 1 << 4,
  Reverse = 1 << 5,
  Hidden = 1 << 6,
  Strike = 1 << 7,
}

export type GeometryLayer = 'background' | 'content' | 'decoration' | 'selection' | 'cursor'
export type GeometryPrimitive = 'background' | 'box' | 'decoration' | 'selection' | 'cursor'

export interface GeometryRect {
  readonly kind: 'rect'
  readonly layer: GeometryLayer
  readonly primitive: GeometryPrimitive
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
  readonly color: GridRgb
  readonly alpha: number
}

export interface GeometryGlyph {
  readonly kind: 'glyph'
  readonly layer: 'content'
  readonly codepoint: number
  readonly variant: 0 | 1 | 2 | 3
  readonly x: number
  readonly y: number
  readonly color: GridRgb
  readonly alpha: number
  readonly embolden: boolean
  readonly shear: boolean
}

// One retained semantic command replaces up to eight procedural arm rects.
// visitGeometry expands it directly into a backend sink without allocating.
export interface GeometryBox extends BoxRectSource {
  readonly kind: 'box'
  readonly layer: 'content'
  readonly spec: BoxSpec
  readonly color: GridRgb
  readonly alpha: number
}

export type GeometryContentCommand = GeometryBox | GeometryGlyph
export type GeometryCommand = GeometryRect | GeometryContentCommand
export type GeometryDrawCommand = GeometryRect | GeometryGlyph

export interface GridGeometryInput {
  readonly cols: number
  readonly rows: number
  readonly cells: readonly GridCell[]
  readonly cursor: GridCursor
}

export interface GridSelection {
  readonly active: boolean
  readonly col0: number
  readonly row0: number
  readonly col1: number
  readonly row1: number
}

export interface BuildGeometryOptions {
  readonly availableVariants?: ReadonlySet<GridFontVariant>
  readonly selection?: GridSelection
  readonly showCursor?: boolean
  readonly suppression?: Uint8Array
}

// The five arrays are the canonical Task8/9 draw order. Content is visited in
// two allocation-free passes (all procedural boxes, then all glyphs) to match
// native presentation regardless of cell order. There is deliberately
// no flat commands getter: it would duplicate up to 400,002 references at the
// maximum valid 500x200 grid size. Backends consume this snapshot through
// visitGeometry so box expansion and ordering stay centralized here.
export interface GridGeometry {
  readonly backgrounds: readonly GeometryRect[]
  readonly content: readonly GeometryContentCommand[]
  readonly decorations: readonly GeometryRect[]
  readonly selection: readonly GeometryRect[]
  readonly cursor: readonly GeometryRect[]
  readonly retainedCommandCount: number
}

export interface GeometryVisitor {
  rect(layer: GeometryLayer, primitive: GeometryPrimitive,
       x: number, y: number, width: number, height: number,
       color: GridRgb, alpha: number): void
  glyph(command: GeometryGlyph): void
}

const DIM_ALPHA = 153
const SELECTION_ALPHA = 77

// Per-build, collision-safe RGB interning copies every retained tuple. Its
// entries are bounded by the examined cells plus constant theme colors and
// cannot alias caller-owned configuration or cell arrays.
class ColorPool {
  readonly #colors = new Map<string, GridRgb>()

  copy(value: GridRgb): GridRgb {
    const key = `${value[0]},${value[1]},${value[2]}`
    const cached = this.#colors.get(key)
    if (cached !== undefined) return cached
    const copied = Object.freeze([value[0], value[1], value[2]]) as GridRgb
    this.#colors.set(key, copied)
    return copied
  }
}

function rect(pool: ColorPool, layer: GeometryLayer, primitive: GeometryPrimitive,
              x: number, y: number, width: number, height: number,
              color: GridRgb, alpha = 255): GeometryRect {
  return Object.freeze({
    kind: 'rect', layer, primitive, x, y, width, height,
    color: pool.copy(color), alpha,
  })
}

interface VariantChoice {
  readonly variant: 0 | 1 | 2 | 3
  readonly embolden: boolean
  readonly shear: boolean
}

const REGULAR = Object.freeze({ variant: 0, embolden: false, shear: false }) satisfies VariantChoice
const BOLD = Object.freeze({ variant: 1, embolden: false, shear: false }) satisfies VariantChoice
const ITALIC = Object.freeze({ variant: 2, embolden: false, shear: false }) satisfies VariantChoice
const BOLD_ITALIC = Object.freeze({ variant: 3, embolden: false, shear: false }) satisfies VariantChoice
const SYNTH_BOLD = Object.freeze({ variant: 0, embolden: true, shear: false }) satisfies VariantChoice
const SYNTH_ITALIC = Object.freeze({ variant: 0, embolden: false, shear: true }) satisfies VariantChoice
const SYNTH_BOTH = Object.freeze({ variant: 0, embolden: true, shear: true }) satisfies VariantChoice
const BOLD_SHEAR = Object.freeze({ variant: 1, embolden: false, shear: true }) satisfies VariantChoice
const ITALIC_EMBOLDEN = Object.freeze({ variant: 2, embolden: true, shear: false }) satisfies VariantChoice

function variant(style: number, available: ReadonlySet<GridFontVariant>): VariantChoice {
  const bold = (style & GridStyle.Bold) !== 0
  const italic = (style & GridStyle.Italic) !== 0
  if (bold && italic) {
    if (available.has('bold-italic')) return BOLD_ITALIC
    if (available.has('bold')) return BOLD_SHEAR
    if (available.has('italic')) return ITALIC_EMBOLDEN
    return SYNTH_BOTH
  }
  if (bold) return available.has('bold') ? BOLD : SYNTH_BOLD
  if (italic) return available.has('italic') ? ITALIC : SYNTH_ITALIC
  return REGULAR
}

function backgroundGeometry(input: GridGeometryInput, config: GridRendererConfig,
                            pool: ColorPool): GeometryRect[] {
  const commands: GeometryRect[] = []
  for (let row = 0; row < input.rows; row++) {
    let runStart = 0
    let runColor: GridRgb | null = null
    const finish = (col: number): void => {
      if (runColor === null) return
      commands.push(rect(pool, 'background', 'background', runStart * config.cellWidth,
        row * config.cellHeight, (col - runStart) * config.cellWidth,
        config.cellHeight, runColor))
      runColor = null
    }
    for (let col = 0; col <= input.cols; col++) {
      let background: GridRgb | null = null
      if (col < input.cols) {
        const cell = input.cells[row * input.cols + col]
        const resolved = resolveCellColors(config, cell.foreground, cell.background,
          (cell.style & GridStyle.Reverse) !== 0).background
        if (!colorsEqual(resolved, config.background)) background = pool.copy(resolved)
      }
      if (runColor !== null && (background === null || !colorsEqual(runColor, background))) finish(col)
      if (background !== null && runColor === null) {
        runStart = col
        runColor = background
      }
    }
  }
  return commands
}

function selectionGeometry(selection: GridSelection | undefined, input: GridGeometryInput,
                           config: GridRendererConfig, pool: ColorPool): GeometryRect[] {
  if (selection === undefined || !selection.active || input.cols === 0 || input.rows === 0 ||
      selection.col0 < 0 || selection.row0 < 0 || selection.col0 >= input.cols || selection.row0 >= input.rows ||
      selection.col1 < selection.col0 || selection.row1 < selection.row0) return []
  const col1 = Math.min(selection.col1, input.cols - 1)
  const row1 = Math.min(selection.row1, input.rows - 1)
  return [rect(pool, 'selection', 'selection', selection.col0 * config.cellWidth,
    selection.row0 * config.cellHeight, (col1 - selection.col0 + 1) * config.cellWidth,
    (row1 - selection.row0 + 1) * config.cellHeight, config.foreground, SELECTION_ALPHA)]
}

function cursorGeometry(cursor: GridCursor, input: GridGeometryInput,
                        config: GridRendererConfig, show: boolean,
                        pool: ColorPool): GeometryRect[] {
  if (!show || !cursor.visible || cursor.col < 0 || cursor.row < 0 ||
      cursor.col >= input.cols || cursor.row >= input.rows) return []
  const x = cursor.col * config.cellWidth
  const y = cursor.row * config.cellHeight
  const thin = Math.max(1, Math.trunc(config.cellHeight / 10))
  if (config.cursorStyle === 'bar') {
    return [rect(pool, 'cursor', 'cursor', x, y, thin, config.cellHeight, config.cursor)]
  }
  if (config.cursorStyle === 'underline') {
    return [rect(pool, 'cursor', 'cursor', x, y + config.cellHeight - thin,
      config.cellWidth, thin, config.cursor)]
  }
  return [rect(pool, 'cursor', 'cursor', x, y,
    config.cellWidth, config.cellHeight, config.cursor)]
}

export function buildGeometry(input: GridGeometryInput, config: GridRendererConfig,
                              options: BuildGeometryOptions = {}): GridGeometry {
  if (!Number.isInteger(input.cols) || !Number.isInteger(input.rows) || input.cols < 0 || input.rows < 0 ||
      input.cells.length !== input.cols * input.rows) throw new RangeError('grid geometry dimensions do not match cells')
  if (options.suppression !== undefined &&
      (!(options.suppression instanceof Uint8Array) ||
       options.suppression.length !== input.cols * input.rows)) {
    throw new RangeError('grid geometry suppression mask does not match cells')
  }
  const available = options.availableVariants ?? new Set(config.fonts.map((font) => font.variant))
  const pool = new ColorPool()
  const backgrounds = backgroundGeometry(input, config, pool)
  const content: GeometryContentCommand[] = []
  const decorations: GeometryRect[] = []
  const lineThickness = Math.max(1, Math.trunc(config.cellHeight / 12))

  for (let row = 0; row < input.rows; row++) {
    for (let col = 0; col < input.cols; col++) {
      const cell = input.cells[row * input.cols + col]
      if (options.suppression !== undefined &&
          options.suppression[row * input.cols + col] !== 0) continue
      if (cell.text === '' || (cell.style & GridStyle.Hidden) !== 0) continue
      const codepoint = cell.text.codePointAt(0)
      if (codepoint === undefined) continue
      const color = pool.copy(resolveCellColors(config, cell.foreground, cell.background,
        (cell.style & GridStyle.Reverse) !== 0).foreground)
      const alpha = (cell.style & GridStyle.Dim) !== 0 ? DIM_ALPHA : 255
      const x = col * config.cellWidth
      const y = row * config.cellHeight
      const procedural = boxSpec(codepoint)
      if (procedural !== null) {
        content.push(Object.freeze({
          kind: 'box', layer: 'content', spec: procedural,
          x, y, width: config.cellWidth, height: config.cellHeight, color, alpha,
        }))
      } else if (codepoint !== 0x20) {
        const choice = variant(cell.style, available)
        content.push(Object.freeze({
          kind: 'glyph', layer: 'content', codepoint, x, y, color, alpha,
          variant: choice.variant, embolden: choice.embolden, shear: choice.shear,
        }))
      }
      if ((cell.style & GridStyle.Underline) !== 0) {
        const maximumY = y + config.cellHeight - lineThickness
        const underlineY = Math.min(y + config.baseline + lineThickness, maximumY)
        decorations.push(rect(pool, 'decoration', 'decoration', x, underlineY,
          config.cellWidth, lineThickness, color, alpha))
      }
      if ((cell.style & GridStyle.Strike) !== 0) {
        decorations.push(rect(pool, 'decoration', 'decoration', x,
          y + Math.trunc(config.cellHeight / 2), config.cellWidth, lineThickness, color, alpha))
      }
      // BLINK is intentionally ignored, matching the native renderer.
    }
  }

  const selection = selectionGeometry(options.selection, input, config, pool)
  const cursor = cursorGeometry(input.cursor, input, config, options.showCursor ?? true, pool)
  const retainedCommandCount = backgrounds.length + content.length + decorations.length +
    selection.length + cursor.length
  return Object.freeze({
    backgrounds: Object.freeze(backgrounds),
    content: Object.freeze(content),
    decorations: Object.freeze(decorations),
    selection: Object.freeze(selection),
    cursor: Object.freeze(cursor),
    retainedCommandCount,
  })
}

function emitStoredRect(visitor: GeometryVisitor, command: GeometryRect): void {
  visitor.rect(command.layer, command.primitive, command.x, command.y,
    command.width, command.height, command.color, command.alpha)
}

function emitProceduralRect(visitor: GeometryVisitor, command: GeometryBox,
                            x: number, y: number, width: number, height: number,
                            primitiveAlpha: number): void {
  visitor.rect('content', 'box', x, y, width, height, command.color,
    command.alpha === 255 ? primitiveAlpha : command.alpha)
}

// Allocation-free ordered consumption contract for Canvas2D and WebGL. It
// expands all procedural boxes before visiting any glyph sprites, then emits
// decorations and overlays. It never constructs a flat command array,
// retained sub-layer arrays, or transient rect objects.
export function visitGeometry(geometry: GridGeometry, visitor: GeometryVisitor): void {
  for (const command of geometry.backgrounds) emitStoredRect(visitor, command)
  for (const command of geometry.content) {
    if (command.kind === 'box') visitBoxRects(command, visitor, emitProceduralRect)
  }
  for (const command of geometry.content) {
    if (command.kind === 'glyph') visitor.glyph(command)
  }
  for (const command of geometry.decorations) emitStoredRect(visitor, command)
  for (const command of geometry.selection) emitStoredRect(visitor, command)
  for (const command of geometry.cursor) emitStoredRect(visitor, command)
}
